import {
  Box,
  Divider,
  Flex,
  SimpleGrid,
  Stack,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import { GatsbyImage } from "gatsby-plugin-image";
import { FC } from "react";
import { BlockContainer, Card, ShiIcon } from "src/atoms";
import { ShiIconName } from "src/atoms/ShiIcon";
import Renderer from "src/lib/renderer";
import RatingsGradeStars from "src/components/GradeStars";

import { Element } from "@prismicio/react";
import paragraph from "src/lib/renderer/elements/paragraph";
import { hyperlink } from "src/lib/renderer/elements/hyperlink";

const CollectionRatings: FC<Gatsby.CollectionRatingsFragment> = ({ data }) => {
  if (!data) throw Error();

  const { content, legend, ratings, has_colored_background, testimonials } =
    data;

  const textVariant = useBreakpointValue({
    base: "body-md-med",
    md: "body-lg-med",
  });

  return (
    <BlockContainer
      paddingY={{
        base: has_colored_background ? "space-40" : "space-20",
        md: has_colored_background ? "space-60" : "space-20",
      }}
    >
      <Box
        bg={has_colored_background ? "secondary-stone-100" : "transparent"}
        borderRadius="intermediate"
        p={{
          base: has_colored_background ? "space-24" : 0,
          md: has_colored_background ? "space-80" : 0,
        }}
        w="full"
      >
        {content?.text && (
          <Box
            marginBottom={{ base: "space-32", md: "space-48" }}
            maxW="800px"
            mx="auto"
          >
            <Renderer field={content} />
          </Box>
        )}

        <Flex
          flexDirection={{ base: "column", lg: "row" }}
          justifyContent="center"
          alignItems="center"
        >
          {/* Mobile only: Heading text at top (hidden on desktop) */}
          <Box
            display={{ base: "block", lg: "none" }}
            marginBottom="space-32"
            width="100%"
            textAlign="center"
            order={{ base: 1 }}
            sx={{
              // Hide the link in this render
              a: {
                display: "none",
              },
            }}
          >
            <Renderer
              overrides={{
                [Element.paragraph]: (args) =>
                  paragraph({
                    ...args,
                    overrideProps: {
                      marginBottom: "2px",
                      variant: "heading-5",
                      sx: {
                        strong: {
                          color: "inherit",
                        },
                      },
                      _last: {
                        marginBottom: 0,
                      },
                    },
                  }),
              }}
              field={legend}
            />
          </Box>

          <SimpleGrid
            columns={{ base: 1, md: Math.min(ratings?.length ?? 0, 3) }}
            sx={{
              "@media screen and (min-width: 425px) and (max-width: 959px)": {
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                // Center the last item if it's alone in the last row (odd number of items in 2-column layout)
                "> *:last-child:nth-child(odd)": {
                  gridColumn: "1 / -1",
                  justifySelf: "center",
                },
              },
            }}
            gridGap={{ base: "space-32", md: "space-80" }}
            marginRight={{ lg: "space-80" }}
            order={{ base: 2, lg: 1 }}
          >
            {ratings?.map((rating) => {
              if (rating?.rate === undefined) return null;

              return (
                <Stack
                  alignItems="center"
                  spacing={{ base: "space-8", md: "space-16" }}
                  key={rating.marketplace}
                >
                  <RatingsGradeStars value={rating.rate} fill="text-primary" />

                  <Text
                    as="div"
                    display="flex"
                    alignItems="center"
                    fontWeight="medium"
                    color="text-primary"
                    flex="1"
                  >
                    <Text as="span" variant={textVariant}>
                      {rating.rate}
                    </Text>

                    <Text
                      as="span"
                      marginX={{ base: "space-6", md: "space-8" }}
                      variant={textVariant}
                    >
                      {rating.marketplace}
                    </Text>

                    <Box h="24px" w="24px">
                      <ShiIcon
                        name={rating.marketplace_icon as ShiIconName}
                        width="20px"
                        height="20px"
                        fill="text-primary"
                      />
                    </Box>
                  </Text>
                </Stack>
              );
            })}
          </SimpleGrid>

          {/* Vertical divider for large breakpoints */}
          <Divider
            marginY="space-16"
            marginX="space-32"
            display={{ base: "none", lg: "inherit" }}
            variant="grey"
            orientation="vertical"
            height="65px"
            order={{ lg: 2 }}
          />

          {/* Desktop: full content | Mobile: only link at bottom */}
          <Box
            textAlign={{ lg: "initial" }}
            marginTop={{ lg: "0" }}
            order={{ base: 3, lg: 3 }}
            sx={{
              ".btn-secondary": {
                borderBottomColor: "text-secondary",
              },
              // On mobile, hide paragraph text but show link
              p: {
                "> span , > strong": {
                  display: { base: "none", lg: "block" },
                },
              },
            }}
          >
            <Renderer
              overrides={{
                [Element.paragraph]: (args) =>
                  paragraph({
                    ...args,
                    overrideProps: {
                      marginBottom: "2px",
                      variant: "heading-5",
                      sx: {
                        strong: {
                          color: "inherit",
                        },
                      },
                      _last: {
                        marginBottom: 0,
                      },
                    },
                  }),
                [Element.hyperlink]: (args) =>
                  hyperlink({
                    ...args,
                    overrideProps: {
                      variant: "tertiary",
                      marginTop: { base: "space-32", lg: "space-16" },
                    },
                  }),
              }}
              field={legend}
            />
          </Box>
        </Flex>

        {testimonials?.length ? (
          <SimpleGrid
            columns={{ base: 1, md: 2 }}
            marginX="auto"
            paddingX={{
              md: "space-40",
            }}
            gridGap="space-32"
            marginTop="space-48"
          >
            {testimonials.map((testimonial) => {
              if (!testimonial) return null;
              const author_picture = testimonial.author_picture?.document;

              return (
                <Card
                  as={Flex}
                  flexDirection="column"
                  gap="space-32"
                  paddingY="space-40"
                  paddingX={{ md: "space-40" }}
                  textAlign="left"
                  position="relative"
                  key={testimonial.author_name}
                  hoverable={false}
                  background={
                    has_colored_background ? "white" : "secondary-stone-100"
                  }
                  borderRadius="intermediate"
                  sx={{
                    p: {
                      _last: {
                        mb: 0,
                      },
                    },
                  }}
                >
                  <ShiIcon
                    name="quotationmark"
                    boxSize="40px"
                    color="primary-green"
                  />

                  <Renderer
                    overrides={{
                      [Element.paragraph]: (args) =>
                        paragraph({
                          ...args,
                          overrideProps: {
                            variant: "body-md-reg",
                            color: "text-primary",
                          },
                        }),
                    }}
                    field={testimonial.testimonial}
                  />

                  <Flex marginTop="auto" alignItems="center">
                    {author_picture?.__typename === "PrismicElementImage" && (
                      <Box
                        as={GatsbyImage}
                        borderRadius="full"
                        boxSize="40px"
                        image={author_picture.data?.image_file?.gatsbyImageData}
                        alt={`Portrait of ${testimonial.author_name}, ${testimonial.author_job}`}
                      />
                    )}

                    <Box marginLeft="space-16" color="text-primary">
                      <Text as="div" display="inline" variant="body-md-reg">
                        {testimonial.author_name}
                      </Text>
                      {testimonial.author_name && testimonial.author_job && (
                        <Text
                          as="div"
                          display="inline"
                          mx="space-4"
                          variant="body-md-reg"
                        >
                          -
                        </Text>
                      )}
                      <Text as="div" display="inline" variant="body-md-reg">
                        {testimonial.author_job}
                      </Text>
                    </Box>
                  </Flex>
                </Card>
              );
            })}
          </SimpleGrid>
        ) : null}
      </Box>
    </BlockContainer>
  );
};

export default CollectionRatings;
